import { Cubit } from "blac-next";
import FileType from "src/constants/fileType";
import Path from "src/constants/path";
import { createAppPath } from "src/lib/createAppPath";
import reportErrorSentry from "src/lib/reportErrorSentry";
import { fileState, toast } from "src/state/state";

import {
  AddMealInsightFeedbackRequest,
  CreateMealInsightResponse,
  MealInsightControllerService
} from "@9amhealth/openapi";

interface MealInsightState {
  loading: boolean;
  fileId?: string;
  mealInsight?: CreateMealInsightResponse;
  error?: Error;
  imageError?: Error;
  uploadProgress?: {
    loaded: number;
    total: number;
    percentage: number;
    phase: "uploading" | "analyzing";
  };
  previewUrl?: string;
}

class MealInsightBloc extends Cubit<MealInsightState> {
  static keepAlive = true;

  constructor() {
    super({
      loading: false
    });
  }

  uploadPhoto = async (file: File): Promise<string | undefined> => {
    // Create preview URL for the image
    const previewUrl = URL.createObjectURL(file);

    this.patch({
      loading: true,
      imageError: undefined,
      previewUrl,
      uploadProgress: {
        loaded: 0,
        total: file.size,
        percentage: 0,
        phase: "uploading"
      }
    });

    try {
      const upload = await fileState.uploadFileWithProgress(
        {
          file,
          fileAttributes: {
            "file.type": FileType.MEAL_INSIGHTS
          }
        },
        false, // Don't show blocking overlay
        {
          onProgress: (loaded, total, percentage) => {
            this.patch({
              uploadProgress: {
                loaded,
                total,
                percentage,
                phase: "uploading"
              }
            });
          }
        }
      );

      if (!upload) {
        throw new Error("Failed to upload file");
      }

      // Upload complete, transition to analyzing phase
      this.patch({
        loading: false,
        fileId: upload.id,
        uploadProgress: {
          loaded: 0,
          total: 100,
          percentage: 0,
          phase: "analyzing"
        }
      });

      return upload.id;
    } catch (error: unknown) {
      this.patch({
        imageError: error as Error,
        uploadProgress: undefined
      });
      reportErrorSentry(error);
    } finally {
      this.patch({ loading: false });
    }
  };

  handleUploadPhoto = async (
    file: File,
    navigate: (path: string, options?: { replace?: boolean }) => void
  ): Promise<void> => {
    const tempPath = `${createAppPath(Path.mealInsights)}/${Path.tmp}`;
    navigate(tempPath);

    const fileId = await this.uploadPhoto(file);

    if (fileId) {
      const targetUrl = `${createAppPath(Path.mealInsights)}/${fileId}`;
      navigate(targetUrl, {
        replace: true
      });
    }
  };

  clearMealInsight = () => {
    this.patch({
      loading: false,
      mealInsight: undefined,
      error: undefined
    });
  };

  generateMealInsight = async (fileId: string) => {
    this.clearMealInsight();

    let progressInterval: ReturnType<typeof setInterval> | undefined;

    try {
      this.patch({
        loading: true,
        uploadProgress: {
          loaded: 0,
          total: 100,
          percentage: 0,
          phase: "analyzing"
        }
      });

      const avgLoadTimeMs = 8000; // Average load time of 10 seconds
      const startTime = Date.now();

      // Simulate progress for AI analysis (fake progress bar)
      progressInterval = setInterval(() => {
        const current = this.state.uploadProgress?.percentage ?? 0;
        if (current < 90) {
          // Gradually increment to 90%
          const elapsed = Date.now() - startTime;
          const simulatedPercentage = Math.min(
            (elapsed / avgLoadTimeMs) * 90,
            90
          );
          this.patch({
            uploadProgress: {
              loaded: simulatedPercentage,
              total: 100,
              percentage: simulatedPercentage,
              phase: "analyzing"
            }
          });
        }
      }, 100);

      const response = await MealInsightControllerService.createMealInsight({
        fileId
      });

      clearInterval(progressInterval);

      this.patch({
        loading: false,
        mealInsight: response.data,
        uploadProgress: undefined
      });
    } catch (error: unknown) {
      clearInterval(progressInterval);
      toast.error("mealInsights.error");
      this.patch({
        loading: false,
        error: error as Error,
        uploadProgress: undefined
      });
      reportErrorSentry(error);
    }
  };

  rateMealInsight = async (rating: AddMealInsightFeedbackRequest.rating) => {
    const mealInsightId = this.state.mealInsight?.id;
    if (!mealInsightId) {
      throw new Error("Meal insight ID is required");
    }

    try {
      await MealInsightControllerService.addMealInsightFeedback(mealInsightId, {
        rating
      });
    } catch (error: unknown) {
      reportErrorSentry(error);
    }
  };
}

export default MealInsightBloc;
